import { SdkMessageTypes } from '../constants';
import { HostedWebContentSdkService } from '../hosted-web-content-sdk';
import {
  BarCodeConfig,
  ImageUri,
  IMediaService,
  MediaInputs,
  MediaResult,
  ITelemetryScenario,
  ITelemetryService,
} from '../interfaces';
import { IWebContentRequestMessage } from '../messages.interface';
import { BaseMessageHandler } from './base-message-handler';
import { defaultSdkError, formulateSdkResponse, onSdkError } from './messageHandlingUtils';

/**
 * Responsible for media capability and its related message requests.
 */

export class MediaMessageHandler extends BaseMessageHandler {
  constructor(
    appSdkHost: HostedWebContentSdkService,
    protected mediaService?: IMediaService,
    telemetryService?: ITelemetryService
  ) {
    super(appSdkHost, telemetryService);
  }

  public initHandlers(): void {
    this.registerHandler(
      SdkMessageTypes.media.selectMedia,
      (_, params: MediaInputs) => formulateSdkResponse(params, this.mediaService!.selectMedia),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.media.selectMedia, error, scenario),
      }
    );
    this.registerHandler(
      SdkMessageTypes.media.captureImage,
      (_) => formulateSdkResponse(captureImageProps, this.mediaService!.selectMedia),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.media.selectMedia, error, scenario),
      }
    );
    this.registerHandler(
      SdkMessageTypes.media.getMedia,
      (messageEvent, param: string) =>
        this.mediaService!.getMedia(param, (mediaResult: MediaResult) =>
          this.postMediaResult(messageEvent.data as IWebContentRequestMessage, mediaResult)
        ),
      { errorCallback: this.onGetMediaError }
    );
    this.registerHandler(
      SdkMessageTypes.media.viewImages,
      (_, params: ImageUri[]) => this.mediaService!.viewImages(params),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.media.viewImages, error, scenario),
      }
    );
    this.registerHandler(
      SdkMessageTypes.media.scanBarCode,
      (_, params: BarCodeConfig) => formulateSdkResponse(params, this.mediaService!.scanBarCode),
      {
        errorCallback: (error, scenario) => onSdkError(SdkMessageTypes.media.scanBarCode, error, scenario),
      }
    );
  }

  protected validateDependencies() {
    if (!this.mediaService) {
      throw 'No mediaService provided';
    }
  }

  private onGetMediaError(error: any, scenario: ITelemetryScenario): Promise<MediaResult> {
    scenario?.fail(`GetMedia failed ${error}`);

    return Promise.resolve({
      error: defaultSdkError,
    });
  }

  private postMediaResult(messageRequest: IWebContentRequestMessage, mediaResult: MediaResult): Promise<void> {
    if (mediaResult.mediaChunk) {
      if (mediaResult.mediaChunk.chunkSequence > 0) {
        this.appSdkHost.postResponseToApp(messageRequest.id, [mediaResult], true);
      } else {
        this.appSdkHost.postResponseToApp(messageRequest.id, [mediaResult], false);
      }
      return Promise.resolve();
    } else {
      return Promise.reject('empty media chunk');
    }
  }
}

const captureImageProps: MediaInputs = {
  mediaType: 1,
  maxMediaCount: 1,
  imageProps: {
    sources: [1, 2],
    startMode: 1,
    ink: true,
    cameraSwitcher: true,
    textSticker: true,
    enableFilter: false,
  },
};
